/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Avatar,
    Box,
    List,
    ListItemText,
    ListItemAvatar,
    ListItemButton,
    Skeleton,
    Typography,
    Chip,
    ListItem,
    TextField,
    InputAdornment,
    IconButton,
    Paper,
    CircularProgress,
    Button,
    Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ClearIcon from '@mui/icons-material/Clear';
import CreateTicketModal from './CreateTicketModal';

import { getListContacts, getListContactsByName, setLoadingContacts } from '../../../actions/contacts/contacts';
import { useToken } from '../../../hooks/useToken';
import { stringAvatar } from '../../../helpers/getStringAvatar';
import { useMsal } from "@azure/msal-react";
import { getTicketsByColumn } from '../../../reducers/kanban/ticketActions';

// Constante para el límite de contactos por página
const CONTACTS_PER_PAGE = 11; // Reducido de 12 a 11 contactos por página

// PRINCIPIO DE RESPONSABILIDAD ÚNICA:
// Separamos cada funcionalidad en hooks independientes

// Hook para validar la longitud de un número telefónico
const usePhoneValidator = () => {
    return useCallback((valor) => {
        if (!valor) return false;
        const longitud = valor.length;
        return longitud >= 4 && longitud <= 13;
    }, []);
};

// Hook para manejar la obtención del token (autenticación)
const useAuthToken = () => {
    const { instance } = useMsal();
    const { tokenRequest, getTokenSilent } = useToken();

    const getToken = useCallback(async () => {
        try {
            const { accessToken } = await instance.acquireTokenSilent(tokenRequest);
            return accessToken;
        } catch (error) {
            console.error("Error al obtener token:", error);
            throw new Error("Error al autenticar: " + (error.message || 'Desconocido'));
        }
    }, [instance, tokenRequest]);

    return { getToken, getTokenSilent };
};

// Hook para manejar la carga de contactos
const useContactsLoader = () => {
    const dispatch = useDispatch();
    const { id_customer } = useSelector(state => state.agent);
    const { getToken } = useAuthToken();

    // Cargar contactos iniciales
    const loadInitialContacts = useCallback(async () => {
        try {
            dispatch(setLoadingContacts(true));
            const accessToken = await getToken();
            await dispatch(getListContacts(id_customer, accessToken, 0, CONTACTS_PER_PAGE));
            return true;
        } catch (error) {
            console.error("Error al cargar contactos iniciales:", error);
            dispatch(setLoadingContacts(false));
            throw error;
        }
    }, [dispatch, id_customer, getToken]);

    // Buscar contactos por término
    const searchContacts = useCallback(async (searchTerm, validatePhone) => {
        if (!searchTerm || searchTerm.trim() === '') {
            return loadInitialContacts();
        }

        try {
            dispatch(setLoadingContacts(true));
            const accessToken = await getToken();

            // Verificar si es un número o texto
            const isNumber = /^\d+$/.test(searchTerm);

            if (isNumber) {
                if (!validatePhone(searchTerm)) {
                    throw new Error("Para buscar por número, debe tener entre 4 y 13 dígitos");
                }
            }

            await dispatch(getListContactsByName(searchTerm, id_customer, accessToken));
            return true;
        } catch (error) {
            console.error("Error en búsqueda:", error);
            dispatch(setLoadingContacts(false));
            throw error;
        }
    }, [dispatch, id_customer, getToken, loadInitialContacts]);

    // Cargar más contactos (paginación)
    const loadMoreContacts = useCallback(async (searchTerm, offset) => {
        try {
            const accessToken = await getToken();

            if (searchTerm && searchTerm.trim() !== '') {
                await dispatch(getListContactsByName(searchTerm, id_customer, accessToken));
            } else {
                await dispatch(getListContacts(id_customer, accessToken, offset, CONTACTS_PER_PAGE));
            }
            return true;
        } catch (error) {
            console.error("Error al cargar más contactos:", error);
            throw error;
        }
    }, [dispatch, id_customer, getToken]);

    return { loadInitialContacts, searchContacts, loadMoreContacts };
};

// Hook para manejar la paginación
const usePagination = (totalItems, itemsPerPage) => {
    const [page, setPage] = useState(1);

    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = Math.min(page * itemsPerPage, totalItems);

    const changePage = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            return true;
        }
        return false;
    }, [totalPages]);

    // Reset para cuando cambia la búsqueda
    const resetPage = useCallback(() => {
        setPage(1);
    }, []);

    return { page, changePage, resetPage, startIndex, endIndex, totalPages };
};

// PRINCIPIO DE ABIERTO/CERRADO:
// Componentes de UI separados para facilitar extensiones
const SearchField = ({ searchTerm, onSearch, onClear, onSearchClick, isLoading, isSearching }) => {
    // Manejar la tecla Enter para búsqueda
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && searchTerm.trim() !== '') {
            onSearchClick();
        }
    };

    // InputProps optimizado para evitar recálculos innecesarios
    const inputProps = React.useMemo(() => ({
        startAdornment: (
            <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
        ),
        endAdornment: searchTerm ? (
            <InputAdornment position="end">
                <IconButton
                    size="small"
                    onClick={onClear}
                    edge="end"
                    aria-label="limpiar búsqueda"
                    disabled={isLoading || isSearching}
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
            </InputAdornment>
        ) : null
    }), [searchTerm, onClear, isLoading, isSearching]);

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
                fullWidth
                placeholder="Buscar nombre o teléfono"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={onSearch}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                InputProps={inputProps}
                sx={{
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 1
                    }
                }}
            />
            <Button
                variant="contained"
                size="small"
                onClick={onSearchClick}
                disabled={isLoading || isSearching || searchTerm.trim() === ''}
                startIcon={isSearching ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    height: '40px', // Mismo alto que el TextField
                    whiteSpace: 'nowrap'
                }}
            >
                {isSearching ? 'Buscando...' : 'Buscar'}
            </Button>
        </Box>
    );
};

const ErrorMessage = ({ error, onRetry }) => {
    if (!error) return null;

    return (
        <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            <Alert
                severity="error"
                variant="filled"
                action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={onRetry}
                    >
                        Reintentar
                    </Button>
                }
            >
                {error}
            </Alert>
        </Box>
    );
};

const ContactItem = ({ contact, isSelected, onContactClick, onCreateTicket }) => {
    const contactId = contact._id || contact.id;
    const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Sin nombre';

    return (
        <ListItem
            disablePadding
            sx={{
                borderRadius: 1,
                mb: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'rgba(0, 0, 0, 0.08)',
                backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'primary.light',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
            }}
            secondaryAction={
                <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => onCreateTicket(contact, e)}
                    title="Crear ticket"
                    sx={{
                        color: 'primary.main',
                        bgcolor: 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                            bgcolor: 'rgba(25, 118, 210, 0.15)',
                        }
                    }}
                >
                    <ConfirmationNumberIcon fontSize="small" />
                </IconButton>
            }
        >
            <ListItemButton onClick={() => onContactClick(contactId)}>
                <ListItemAvatar>
                    <Avatar
                        {...stringAvatar(fullName)}
                        sx={{
                            width: 36,
                            height: 36
                        }}
                    />
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Typography variant="subtitle2" noWrap>
                            {fullName}
                        </Typography>
                    }
                    secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {contact.phone || 'Sin teléfono'}
                        </Typography>
                    }
                />
            </ListItemButton>
        </ListItem>
    );
};

const NoResults = ({ searchTerm, onReset }) => (
    <Box display="flex" justifyContent="center" alignItems="center" p={2} flexDirection="column">
        <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            No se encontraron contactos
            {searchTerm ? ` para "${searchTerm}"` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Puedes usar el buscador para encontrar contactos específicos
        </Typography>
        <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onReset}
        >
            Ver todos los contactos
        </Button>
    </Box>
);

const LoadingSkeleton = () => (
    <>
        {Array(5).fill(0).map((_, index) => (
            <ListItem key={`skeleton-${index}`} sx={{ borderRadius: 1, mb: 0.5, p: 1 }}>
                <ListItemAvatar>
                    <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                    primary={<Skeleton variant="text" width={120} />}
                    secondary={<Skeleton variant="text" width={80} />}
                />
                <Skeleton variant="circular" width={24} height={24} />
            </ListItem>
        ))}
    </>
);

const Pagination = ({ currentPage, totalPages, onPageChange, hasMore, isLoading, loadingMore }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            bottom: 0,
            zIndex: 1
        }}
    >
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                mb: 1
            }}
        >
            <Box sx={{ width: '40px' }}></Box> {/* Espaciador izquierdo */}

            <Typography variant="body2" color="text.secondary">
                Página {currentPage} de {totalPages || 1}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                    size="small"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={(currentPage >= totalPages) || isLoading || loadingMore || !hasMore}
                    sx={{
                        bgcolor: 'background.default',
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                >
                    <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>

        {!hasMore && !isLoading && !loadingMore && (
            <Typography variant="caption" color="text.secondary">
                No hay más contactos para mostrar
            </Typography>
        )}

        {loadingMore && (
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ my: 1 }}>
                <CircularProgress size={20} color="primary" sx={{ mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                    Cargando contactos...
                </Typography>
            </Box>
        )}
    </Box>
);

// Componente principal de la barra lateral de contactos para Kanban
const KanbanContactsSidebar = ({ boardId, boardType }) => {
    // Hooks - SIEMPRE deben definirse primero y sin condiciones
    const dispatch = useDispatch();
    const { getTokenSilent } = useAuthToken();

    // Estado para manejar los contactos y la búsqueda
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [error, setError] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [displayedContacts, setDisplayedContacts] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // Estado para el modal de creación de tickets
    const [ticketModalOpen, setTicketModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);

    // Referencia al contenedor de scroll
    const scrollRef = useRef(null);

    // Hooks personalizados
    const validatePhone = usePhoneValidator();
    const { loadInitialContacts, searchContacts, loadMoreContacts } = useContactsLoader();

    // Estado y selectors de Redux
    const { contacts = [], loading = false, count = 0 } = useSelector(state => state.contact || { contacts: [], loading: false, count: 0 });

    // Normalizar el tipo de tablero para asegurar compatibilidad
    const normalizedBoardType = boardType === 'support' ? 'soporte' : boardType;

    // Verificar si el tipo de tablero es soportado
    const isSupportedType = ['general', 'soporte'].includes(normalizedBoardType);

    // Eliminar duplicados de contactos (soluciona el warning de keys duplicadas)
    const uniqueContacts = useMemo(() => {
        const seen = new Set();
        return contacts.filter(contact => {
            const id = contact._id || contact.id;
            if (!id || seen.has(id)) {
                return false;
            }
            seen.add(id);
            return true;
        });
    }, [contacts]);

    // Determinar si hay más contactos para cargar
    const hasMore = uniqueContacts.length < count;

    // Hook de paginación
    const { page, changePage, resetPage, totalPages } = usePagination(count, CONTACTS_PER_PAGE);

    // Efecto para actualizar los contactos mostrados cuando cambian los contactos o la página
    useEffect(() => {
        // Si no hay contactos, no hacemos nada
        if (!uniqueContacts || uniqueContacts.length === 0) {
            setDisplayedContacts([]);
            return;
        }

        const start = (page - 1) * CONTACTS_PER_PAGE;
        const end = Math.min(page * CONTACTS_PER_PAGE, uniqueContacts.length);

        // Comprobamos si realmente necesitamos actualizar los contactos mostrados
        const newDisplayedContacts = uniqueContacts.slice(start, end);

        // Solo actualizamos el estado si realmente hay cambios
        if (JSON.stringify(newDisplayedContacts) !== JSON.stringify(displayedContacts)) {
            setDisplayedContacts(newDisplayedContacts);
        }
    }, [uniqueContacts, page, displayedContacts]);

    // Cargar los primeros contactos al iniciar
    useEffect(() => {
        if (!loading && uniqueContacts.length === 0 && isSupportedType) {
            setError(null);
            loadInitialContacts()
                .catch(error => {
                    setError(error.message || "Error inesperado al cargar contactos");
                });
        }
    }, [loading, uniqueContacts.length, loadInitialContacts, isSupportedType]);

    // PRINCIPIO DE SEGREGACIÓN DE INTERFACES:
    // Funciones específicas para cada tarea

    // Función para manejar solo el cambio del input (sin búsqueda automática)
    const handleSearch = useCallback((e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
    }, []);

    // Función para ejecutar la búsqueda cuando el usuario hace clic en buscar
    const handleSearchClick = useCallback(async () => {
        if (!searchTerm || searchTerm.trim() === '') {
            return;
        }

        setIsSearching(true);
        setError(null);
        resetPage();

        try {
            await searchContacts(searchTerm.trim(), validatePhone);
        } catch (error) {
            setError(error.message || "Error en la búsqueda");
        } finally {
            setIsSearching(false);
        }
    }, [searchTerm, searchContacts, validatePhone, resetPage]);

    // Manejar la limpieza de la búsqueda
    const handleClearSearch = useCallback(() => {
        setSearchTerm('');
        resetPage();
        setError(null);
        setIsSearching(false);

        loadInitialContacts()
            .catch(error => {
                setError(error.message || "Error al cargar contactos");
            });
    }, [loadInitialContacts, resetPage]);

    // Función para reintentar la carga después de un error
    const handleRetry = useCallback(() => {
        setError(null);
        setIsSearching(false);
        
        if (searchTerm && searchTerm.trim() !== '') {
            handleSearchClick();
        } else {
            loadInitialContacts()
                .catch(error => {
                    setError(error.message || "Error al cargar contactos");
                });
        }
    }, [searchTerm, handleSearchClick, loadInitialContacts]);

    // Función para manejar el cambio de página
    const handlePageChange = useCallback((newPage) => {
        if (newPage === page) return;

        // Si la nueva página requiere cargar más contactos
        const requiredContacts = newPage * CONTACTS_PER_PAGE;

        if (requiredContacts > uniqueContacts.length && hasMore && !loading && !loadingMore) {
            setLoadingMore(true);
            setError(null);

            loadMoreContacts(searchTerm, uniqueContacts.length)
                .then(() => {
                    changePage(newPage);
                    setLoadingMore(false);
                })
                .catch(error => {
                    setLoadingMore(false);
                    setError(error.message || "Error al cargar más contactos");
                });
        } else {
            // Si ya tenemos suficientes contactos cargados, solo cambiamos la página
            changePage(newPage);
        }
    }, [page, loading, hasMore, loadingMore, uniqueContacts, searchTerm, changePage, loadMoreContacts]);

    // Detectar cuando el scroll llega al final
    const handleScroll = useCallback((e) => {
        const container = e.target;
        const { scrollTop, scrollHeight, clientHeight } = container;

        // Si estamos cerca del final (a 100px), cargamos más
        if (scrollHeight - scrollTop - clientHeight < 100 && !loading && hasMore && !loadingMore) {
            setLoadingMore(true);

            loadMoreContacts(searchTerm, uniqueContacts.length)
                .then(() => {
                    setLoadingMore(false);
                })
                .catch(error => {
                    setLoadingMore(false);
                    setError(error.message || "Error al cargar más contactos");
                });
        }
    }, [loading, hasMore, loadingMore, searchTerm, uniqueContacts.length, loadMoreContacts]);

    // Manejar la selección de un contacto
    const handleContactClick = useCallback((contactId) => {
        setSelectedContactId(contactId === selectedContactId ? null : contactId);
    }, [selectedContactId]);

    // Manejar la creación de un ticket
    const handleCreateTicket = useCallback((contact, event) => {
        if (event) {
            event.stopPropagation();
        }

        // Guardar el contacto seleccionado y abrir el modal
        setSelectedContact(contact);
        setTicketModalOpen(true);
    }, []);

    // Cerrar el modal de creación de tickets
    const handleCloseTicketModal = () => {
  
        setTicketModalOpen(false);
    };

    // Manejar el éxito en la creación de un ticket
    const handleTicketCreationSuccess = async (ticket) => {

        
        // Cerrar el modal de creación de tickets
        setTicketModalOpen(false);
        
        // Actualizar los tickets de la columna
        try {
            const token = await getTokenSilent();
            if (token) {
                await dispatch(getTicketsByColumn(ticket.column_id, token));
            }
        } catch (error) {
            console.warn('No se pudieron actualizar los tickets después de crear:', error);
        }
    };

    // Solo renderizar el componente para tableros tipo general o soporte
    if (!isSupportedType) {
        return (
            <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" align="center">
                    Este tipo de tablero no soporta contactos
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={3}
            sx={{
                height: 'calc(100vh - 200px)', // Mucho más alto para el sidebar
                maxHeight: 'calc(100vh - 200px)', // Máximo más alto
                minHeight: 'calc(100vh - 200px)', // Mínimo más alto
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                position: 'relative'
            }}
        >
            {/* Cabecera fija */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                }}
            >
                <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    display="flex"
                    alignItems="center"
                >
                    <PersonOutlineIcon sx={{ mr: 0.5, fontSize: 20, color: 'primary.main' }} />
                    Contactos
                    <Chip
                        label={`${count > 0 ? `${displayedContacts.length} de ${count}` : '0'}`}
                        size="small"
                        sx={{
                            ml: 1,
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: 'primary.main',
                            color: 'white'
                        }}
                    />
                </Typography>
            </Box>

            {/* Búsqueda fija */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    top: '64px',
                    bgcolor: 'background.paper',
                    zIndex: 1
                }}
            >
                <SearchField
                    searchTerm={searchTerm}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    onSearchClick={handleSearchClick}
                    isLoading={loading}
                    isSearching={isSearching}
                />
                
                {/* Mensaje informativo */}
                <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                        display: 'block', 
                        mt: 0.5, 
                        fontSize: '0.7rem',
                        fontStyle: 'italic'
                    }}
                >
                    💡 Escribe el nombre o teléfono y presiona "Buscar" o Enter
                </Typography>
            </Box>

            {/* Mensaje de error si existe */}
            <ErrorMessage
                error={error}
                onRetry={handleRetry}
            />

            {/* Lista de contactos con scroll */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'background.paper',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.300',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: 'grey.400',
                        }
                    }}
                    onScroll={handleScroll}
                    ref={scrollRef}
                >
                    <List sx={{ p: 1 }}>
                        {loading && displayedContacts.length === 0 ? (
                            <LoadingSkeleton />
                        ) : displayedContacts.length === 0 ? (
                            <NoResults
                                searchTerm={searchTerm}
                                onReset={handleClearSearch}
                            />
                        ) : (
                            <>
                                {displayedContacts.map((contact) => (
                                    <ContactItem
                                        key={`contact-${contact._id || contact.id || Math.random().toString(36).substring(2)}`}
                                        contact={contact}
                                        isSelected={selectedContactId === (contact._id || contact.id)}
                                        onContactClick={handleContactClick}
                                        onCreateTicket={handleCreateTicket}
                                    />
                                ))}
                            </>
                        )}
                    </List>
                </Box>
            </Box>

            {/* Controles de paginación (siempre visibles si hay contactos) */}
            {displayedContacts.length > 0 && (
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    hasMore={hasMore}
                    isLoading={loading}
                    loadingMore={loadingMore}
                />
            )}

            {/* Modal de creación de tickets */}
            <CreateTicketModal
                open={ticketModalOpen}
                onClose={handleCloseTicketModal}
                onSuccess={handleTicketCreationSuccess}
                contact={selectedContact}
                boardType={normalizedBoardType}
                boardId={boardId}
            />
        </Paper>
    );
};

export default KanbanContactsSidebar;